/**
 * API client for ИИ-Колхоз FMS — fetch-based, mirrors frontend/src/lib/api.ts
 * с теми же refresh-on-401 паттернами (юзер 2026-04-27, AK-32).
 *
 * Ключевые отличия от frontend/:
 *   - Vite использует import.meta.env (vs process.env в Next.js).
 *   - В dev режиме /api проксируется через vite.config.ts на :8000;
 *     в production API_URL может быть пустым (same-origin) или абсолютным.
 *   - typeof window guards оставлены — на случай SSR-будущего.
 */
import {
  clearImpersonationSnapshot,
  isImpersonationActive,
} from '@/lib/auth/impersonationSession';
import { isPublicPath } from '@/lib/auth/publicPaths';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export class ApiError extends Error {
  /**
   * detail — всегда string (совместимо с `message.error(e.detail)` во всём коде).
   * rawDetail — исходный detail из backend: может быть string или object
   * (FastAPI сериализует HTTPException.detail как-есть). Interceptors могут
   * проверить структурированные коды, например `rawDetail?.code === "season_locked"`.
   */
  constructor(
    public status: number,
    public detail: string,
    public rawDetail?: unknown,
  ) {
    super(detail);
  }
}

function coerceDetail(raw: unknown, fallback: string): string {
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') {
    const obj = raw as { message?: unknown; code?: unknown };
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.code === 'string') return obj.code;
    try {
      return JSON.stringify(raw);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

/**
 * Заголовок ответа `/auth/refresh` — единственный сигнал, что режим «вы
 * работаете под …» закончился сам: вышло окно либо сессию режима отозвали
 * (backend `api/v1/auth.py::IMPERSONATION_STATE_HEADER`). Ошибкой сервер это
 * намеренно не делает: ничего страшного не случилось, админ просто вернулся
 * в свою учётку, а 401/409 увели бы интерфейс в разлогин или в цикл повторов.
 *
 * Читается он только потому, что фронт и API живут на одном origin
 * (`VITE_API_URL` пуст во всех наших сборках, в dev — vite-прокси). Если
 * когда-нибудь API вынесут на отдельный домен, заголовок исчезнет из
 * видимости JS, пока backend не добавит его в `expose_headers` CORS — режим
 * снова начнёт молча переживать своё окно. Менять `VITE_API_URL` на
 * абсолютный, не проверив это, нельзя.
 */
const IMPERSONATION_STATE_HEADER = 'X-Impersonation-State';

interface RefreshResult {
  ok: boolean;
  /** Сервер сообщил, что режим «вы работаете под …» закончился. */
  impersonationEnded: boolean;
}

let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * Единственный источник refresh-вызовов.
 * Любой код (interceptor, proactive keepalive в AppShell, и т.п.) ОБЯЗАН
 * ходить через этот lock — иначе backend ловит concurrent refresh с одной
 * cookie R1, считает reuse, revoke-ает сессию, юзер получает silent 401
 * каскадом (AK-79).
 */
async function refreshSession(): Promise<RefreshResult> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const r = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      const ended = r.headers.get(IMPERSONATION_STATE_HEADER) === 'ended';
      if (ended) endImpersonationFromServer();
      return { ok: r.ok, impersonationEnded: ended };
    } catch {
      return { ok: false, impersonationEnded: false };
    } finally {
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

/**
 * Продлить сессию. Обёртка над общим lock'ом для вызывающих, которым важен
 * только факт «жива или нет» (проактивный keepalive в AppShell).
 */
export async function tryRefresh(): Promise<boolean> {
  return (await refreshSession()).ok;
}

/**
 * Сервер закрыл режим сам — снимаем плашку и уходим туда, откуда в режим входят.
 *
 * `exit-impersonation` здесь НЕ зовём: сервер уже погасил сессию режима,
 * записал в аудит `impersonation.user.end` и снял `original_access_token` —
 * возвращать нечего, лишний вызов только шумел бы в журнале.
 *
 * Почему уход на список компаний, а не перезагрузка текущего адреса. После
 * закрытия режима в cookie лежит собственный токен админа. Перезагрузи мы
 * `/fields/123` целевой компании — человек увидел бы знакомый адрес уже
 * своими глазами: в лучшем случае 403, в худшем — свои данные там, где он
 * ждёт чужие. Это ровно та подмена личности, от которой режим и защищают.
 * Уход на `/platform-admin/companies` — это тоже полная перезагрузка
 * страницы (плашка пересобирается с нуля из пустого снимка), но приземляет
 * туда же, куда и выход по истёкшему токену: оба конца режима ведут в одно
 * место, а не в два разных.
 */
function endImpersonationFromServer(): void {
  if (typeof window === 'undefined') return;
  // Заголовок мог прийти на вкладку, где плашки и не было (снимок не записался
  // в private-режиме, вкладку открыли заново) — тогда уводить некого.
  const wasActive = isImpersonationActive();
  clearImpersonationSnapshot();
  if (wasActive) {
    window.location.href = '/platform-admin/companies';
  }
}

async function rawRequest(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT',
  path: string,
  body?: unknown,
): Promise<Response> {
  return fetch(`${API_URL}${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT',
  path: string,
  body?: unknown,
): Promise<T> {
  let resp = await rawRequest(method, path, body);

  // exit-impersonation в списке намеренно: это и есть выход из режима.
  // Кнопка «Выйти» в плашке зовёт его напрямую, и если сервер ответит 401 —
  // продлевать сессию и повторять выход некуда и незачем: человек в любом
  // случае уходит из режима, а `/auth/me` на новой странице доведёт его до
  // `/login`, если умерла и собственная сессия.
  const skipRefresh =
    path.startsWith('/api/v1/auth/refresh') ||
    path.startsWith('/api/v1/auth/login') ||
    path.startsWith('/api/v1/auth/logout') ||
    path.startsWith('/api/v1/auth/exit-impersonation');
  if (resp.status === 401 && !skipRefresh) {
    const peekData = await resp
      .clone()
      .json()
      .catch(() => ({}));
    if (
      peekData?.detail === 'tenant_suspended' &&
      typeof window !== 'undefined'
    ) {
      if (!window.location.pathname.startsWith('/tenant-suspended')) {
        window.location.href = '/tenant-suspended';
      }
      throw new ApiError(401, 'tenant_suspended');
    }

    // AK-470. Под чужой учёткой refresh снова законен: сервер обновляет
    // сессию с оглядкой на режим — либо перевыпускает токен режима, не
    // проворачивая refresh админа, либо честно объявляет заголовком, что
    // режим закончился. Запрещать здесь refresh (как было до контракта)
    // теперь значило бы обрывать режим на половине окна: токен режима живёт
    // 15 минут, само окно вдвое дольше, так что первый же 401 внутри окна
    // выкидывал бы человека без всякой причины.
    const { ok, impersonationEnded } = await refreshSession();

    // Режим закрыт сервером — повторять запрос нечем: в cookie уже
    // собственный токен админа, ответ пришёл бы от его имени. Уход из режима
    // (снимок + переход) уже запущен внутри refreshSession().
    if (impersonationEnded) {
      throw new ApiError(401, 'impersonation_expired');
    }

    if (ok) {
      resp = await rawRequest(method, path, body);
    } else if (typeof window !== 'undefined') {
      // Умерла и собственная сессия. Снимок обязан умереть раньше перехода:
      // иначе после нового входа в этой же вкладке плашка соврёт про режим,
      // которого уже нет.
      clearImpersonationSnapshot();
      // Публичные страницы 401 не удивляет: человек там ещё не входил.
      // Уводить его оттуда нельзя — именно так оборвалась навигация к ручке
      // приглашения (прод 21.08.2026, nginx записал 499), и именно так
      // «приглашение уже использовано» подменялось немым экраном входа.
      if (!isPublicPath(window.location.pathname)) {
        window.location.href = '/login';
      }
    }
  }

  if (resp.status === 204) {
    return undefined as T;
  }
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const rawDetail = data?.detail;
    if (resp.status === 423) {
      const msg =
        rawDetail === 'tenant_restricted'
          ? 'Доступ к компании ограничен (оплата просрочена). Изменения запрещены.'
          : coerceDetail(rawDetail, 'Locked');
      throw new ApiError(423, msg, rawDetail);
    }
    if (resp.status === 403 && rawDetail && typeof rawDetail === 'object') {
      const obj = rawDetail as { code?: string; message?: string };
      if (obj.code === 'season_locked') {
        const msg =
          obj.message ||
          'Сезон не текущий. Разблокируйте редактирование 🔓 в шапке.';
        throw new ApiError(403, msg, rawDetail);
      }
    }
    throw new ApiError(
      resp.status,
      coerceDetail(rawDetail, 'Request failed'),
      rawDetail,
    );
  }
  return data as T;
}

/**
 * Upload a file via multipart/form-data. Cannot use `request()` which sets
 * Content-Type: application/json — for multipart we let the browser set the
 * boundary automatically by passing FormData without a Content-Type header.
 * Includes the same 401→refresh logic as request(). (AK-246/AK-257)
 */
async function uploadFile<T>(path: string, formData: FormData): Promise<T> {
  async function rawUpload(): Promise<Response> {
    return fetch(`${API_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      // No Content-Type header — browser sets multipart/form-data with boundary.
      body: formData,
    });
  }

  let resp = await rawUpload();

  if (resp.status === 401) {
    // Та же развилка, что и в request(): режим жив — продлеваем и повторяем,
    // режим закрыт сервером — не повторяем вовсе, иначе файл дописался бы от
    // имени админа в чужую компанию.
    const { ok, impersonationEnded } = await refreshSession();
    if (impersonationEnded) {
      throw new ApiError(401, 'impersonation_expired');
    }
    if (ok) {
      resp = await rawUpload();
    } else if (typeof window !== 'undefined') {
      clearImpersonationSnapshot();
      // Публичные страницы 401 не удивляет: человек там ещё не входил.
      // Уводить его оттуда нельзя — именно так оборвалась навигация к ручке
      // приглашения (прод 21.08.2026, nginx записал 499), и именно так
      // «приглашение уже использовано» подменялось немым экраном входа.
      if (!isPublicPath(window.location.pathname)) {
        window.location.href = '/login';
      }
    }
  }

  if (resp.status === 204) {
    return undefined as T;
  }
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const rawDetail = data?.detail;
    throw new ApiError(
      resp.status,
      coerceDetail(rawDetail, 'Upload failed'),
      rawDetail,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  uploadFile: <T>(path: string, formData: FormData) =>
    uploadFile<T>(path, formData),
};

export { API_URL };
